import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { registerConfirmHandler, ConfirmOptions, ConfirmResult } from '@/utils/confirmService';

interface ConfirmState extends ConfirmOptions {
  open: boolean;
  title: string;
  description: string;
  variant: 'danger' | 'default';
}

export function ConfirmProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<ConfirmState>({
    open: false, title: '', description: '', variant: 'default',
  });
  const resolverRef = useRef<((v: ConfirmResult) => void) | null>(null);

  useEffect(() => {
    registerConfirmHandler((title, description, options?: ConfirmOptions) => {
      return new Promise<ConfirmResult>(resolve => {
        resolverRef.current = resolve;
        setState({
          open: true,
          title,
          description,
          ...options,
          variant: options?.variant ?? 'danger',
        });
      });
    });
    return () => registerConfirmHandler(null);
  }, []);

  const finish = useCallback((confirmed: boolean, reason = '') => {
    resolverRef.current?.({ confirmed, reason });
    resolverRef.current = null;
    setState(prev => ({ ...prev, open: false }));
  }, []);

  return (
    <>
      {children}
      <ConfirmDialog
        open={state.open}
        title={state.title}
        description={state.description}
        variant={state.variant}
        confirmLabel={state.confirmLabel}
        cancelLabel={state.cancelLabel}
        typeToConfirm={state.typeToConfirm}
        reasonPresets={state.reasonPresets}
        reasonLabel={state.reasonLabel}
        reasonRequired={state.reasonRequired}
        onConfirm={(reason) => finish(true, reason)}
        onCancel={() => finish(false)}
      />
    </>
  );
}
